import Uploader from '../../Builder/components/Uploader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faToggleOff, faToggleOn } from '@fortawesome/free-solid-svg-icons';
import { useEffect, useState } from 'react';
import * as Yup from 'yup';
import { FormikHelpers, useFormik } from 'formik';
import SubmitButton from '../../../components/Common/SubmitButton';
import { handleArtStyleSubmitService } from './services/AdminForms.service';
import { SecureImg } from '../../../components/Common/SecureImage';

interface ArtStyleDetails {
  id?: string;
  name?: string;
  sub_prompt?: string;
  sref_image?: string;
  use_sref?: boolean;
  main_art_character_description?: string;
  name_in_scene?: string;
}

interface ArtStyleFormProps {
  setShowForm: (value: boolean) => void;
  artStyleDetails: ArtStyleDetails | null;
  getArtStyles: (setArtStyles: React.Dispatch<React.SetStateAction<any>>) => void;
  setArtStyles: React.Dispatch<React.SetStateAction<any>>;
}

interface FormValues {
  artStyleId: string | null;
  name: string;
  subPrompt: string;
  srefImage: File | null;
  use_sref: boolean;
  mainArtCharacterDescription?: string;
  nameInScene?: string;
}

export const ArtStyleForm: React.FC<ArtStyleFormProps> = ({
  setShowForm,
  artStyleDetails,
  getArtStyles,
  setArtStyles,
}) => {
  const [loader, setLoader] = useState<boolean>(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [uploadedImageUrl, setUploadedImageUrl] = useState<string>('');
  const [currentPortrait, setCurrentPortrait] = useState<string | null>(null);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required('Art Style name is required'),
    subPrompt: Yup.string(),
    srefImage: Yup.mixed(),
    mainArtCharacterDescription: Yup.string().optional(),
    nameInScene: Yup.string().optional(),
  });

  useEffect(() => {
    setPreviewImage(artStyleDetails?.sref_image || null);
  }, [artStyleDetails]);

  const formik = useFormik<FormValues>({
    initialValues: {
      artStyleId: null,
      name: '',
      subPrompt: '',
      srefImage: null,
      use_sref: false,
      mainArtCharacterDescription: '',
      nameInScene: '',
    },
    validationSchema: validationSchema,
    enableReinitialize: true,
    onSubmit: (values, { resetForm }: FormikHelpers<FormValues>) => {
      handleSubmit(resetForm);
    },
  });

  const handleSubmit = async (resetForm: any) => {
    await handleArtStyleSubmitService(formik.values, artStyleDetails, resetForm, setLoader, setArtStyles, setShowForm);
  };
  useEffect(() => {
    if (artStyleDetails) {
      formik.setValues({
        artStyleId: artStyleDetails.id || null,
        name: artStyleDetails.name || '',
        subPrompt: artStyleDetails.sub_prompt || '',
        srefImage: artStyleDetails.sref_image as File | any,
        use_sref: artStyleDetails.use_sref || false,
        mainArtCharacterDescription: artStyleDetails.main_art_character_description || '',
        nameInScene: artStyleDetails.name_in_scene || '',
      });
    }
  }, [artStyleDetails]);

  const handleImageChange = (input: React.ChangeEvent<HTMLInputElement> | File) => {
    const file = input instanceof File ? input : input.target.files?.[0];

    if (!file) {
      return;
    }

    setSelectedImage(file);
    setUploadedImageUrl('');
    setCurrentPortrait(null);
    formik.setFieldValue('srefImage', file);
  };

  return (
    <div className="bg-storytan mx-auto">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-storyblue text-xl font-bold text-start mb-4">
          {artStyleDetails ? `Edit: ${artStyleDetails.name}` : 'Create New Art Style'}
        </h1>
        <form
          id="form-admin-artStyles"
          name="form-admin-artStyles"
          className="text-start"
          onSubmit={formik.handleSubmit}
        >
          <div className="flex flex-col gap-2 md:gap-4">
            <div className="flex flex-col gap-2">
              <div className="flex flex-col md:flex-row gap-2 w-full">
                <div className="flex flex-col gap-2 w-full">
                  <label className="font-medium">Art Style Name</label>
                  <input
                    type="text"
                    name="name"
                    value={formik.values.name}
                    onChange={formik.handleChange}
                    placeholder="Art Style Name"
                    className="p-2 w-full rounded-md"
                    autoFocus
                  />
                  <div className="text-red-600 mt-1">{formik.errors.name}</div>
                </div>
                <div className="flex flex-col gap-2 w-full">
                  <label className="font-medium">Art Style Name (in scene prompt)</label>
                  <input
                    type="text"
                    name="nameInScene"
                    value={formik.values.nameInScene}
                    onChange={formik.handleChange}
                    placeholder="Art Style Name"
                    className="p-2 w-full rounded-md"
                    autoFocus
                  />
                  <div className="text-red-600 mt-1">{formik.errors.nameInScene}</div>
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-medium">Art Style prompt (for character generation)</label>
              <textarea
                name="mainArtCharacterDescription"
                rows={3}
                value={formik.values.mainArtCharacterDescription}
                onChange={formik.handleChange}
                placeholder="Enter prompt for this art style"
                className="p-2 w-full rounded-md"
              />
              <div className="text-red-600 mt-1">{formik.errors.mainArtCharacterDescription}</div>
            </div>
            <div className="flex flex-col gap-2">
              <label className="font-medium">Art Style Prompt (old img gen tools)</label>
              <textarea
                name="subPrompt"
                rows={3}
                value={formik.values.subPrompt}
                onChange={formik.handleChange}
                placeholder="Art Style Prompt"
                className="p-2 w-full rounded-md"
              />
              <div className="text-red-600 mt-1">{formik.errors.subPrompt}</div>
            </div>
            <div className="flex gap-2 items-center">
              <label className="font-medium">Use sref:</label>
              <FontAwesomeIcon
                icon={formik.values.use_sref ? faToggleOn : faToggleOff}
                className={`h-8 w-8 cursor-pointer ${formik.values.use_sref ? 'text-storygreen' : 'text-red-500'}`}
                title={formik.values.use_sref ? 'Deactivate sref' : 'Activate sref'}
                onClick={() => formik.setFieldValue('use_sref', !formik.values.use_sref)}
              />
            </div>
            <div className="w-full flex flex-col gap-2 md:flex-row items-center">
              <div className="flex flex-col gap-2 w-full md:w-[45%]">
                <label className="font-medium">Art Style Image</label>
                <Uploader
                  onFileChange={handleImageChange}
                  setPreviewImage={setPreviewImage}
                  title="Upload Art Style Image"
                />
                <div className="text-red-600 mt-1">{formik.errors.srefImage}</div>
              </div>
              <div className="mx-auto rounded-md">
                {previewImage && (
                  <SecureImg
                    storageSrc={previewImage}
                    alt="preview"
                    className="mt-8 w-[20rem] h-[20rem] rounded-md object-contain mx-auto"
                  />
                )}
              </div>
            </div>
          </div>
          <SubmitButton title="Save" loader={loader} cancelOperation={setShowForm} />
        </form>
      </div>
    </div>
  );
};
